<!--
  BracketMatchCard.vue

  Individual match card component for tournament brackets.
  Displays team colors, logos, scores, and penalties with custom styling.
  Size: 210px × 47px

  See: app/composables/bracket/README.md for full documentation
-->
<script setup lang="ts">
import type { Match } from "~/src/match/domain/models/Match";
import type { Team } from "~/src/team/domain/models/Team";
import { setAliasUrl, slugify } from "~/src/Shared/utils";
import nuxtLink from "#app/components/nuxt-link";

type Props = {
  match: Match | null;
  region: string;
  teams?: Record<number, Team>;
};

const props = defineProps<Props>();

const localePath = useLocalePath();

const homeTeam = computed(() => {
  if (!props.match || !props.teams) return undefined;
  const homeId = props.match.participants?.homeTeamId;
  return homeId ? props.teams[homeId] : undefined;
});

const awayTeam = computed(() => {
  if (!props.match || !props.teams) return undefined;
  const awayId = props.match.participants?.awayTeamId;
  return awayId ? props.teams[awayId] : undefined;
});
</script>

<template>
  <component
    :is="match ? nuxtLink : 'div'"
    no-prefetch
    :to="
      localePath({
        name: 'region-matches-matchId-homeTeam-vs-awayTeam',
        params: {
          region: region,
          matchId: match?.id,
          homeTeam: slugify(homeTeam?.shortName || 'tbd'),
          awayTeam: slugify(awayTeam?.shortName || 'tbd'),
        },
      })
    "
    class="bracket-match-wrapper relative w-[210px] h-[47px] bg-gray-900 flex items-center justify-center rtl:flex-row-reverse z-[2] hover:scale-105 transition-transform duration-150"
  >
    <!-- HOME box (left side) -->
    <div
      v-if="
        match?.metaInformation.extra_card &&
        match?.metaInformation.extra_card.split(',').some(id => id === homeTeam?.id.toString())
      "
    >
      <img
        src="/img/secret-card/extra-card-kings.png"
        alt="Secret Card"
        class="w-9 h-4 object-cover object-top absolute ltr:left-4 rtl:right-4 bottom-full"
        loading="lazy"
      />
    </div>
    <div class="absolute ltr:left-0 rtl:right-0 top-0 bottom-0 flex">
      <!-- HOME lateral stripe with skew -->
      <div
        class="w-[6.7px] h-full ltr:skew-x-6 rtl:-skew-x-6 -me-1 z-[2]"
        :style="
          homeTeam && homeTeam.firstColorHEX
            ? {
                background: `${homeTeam.firstColorHEX}`,
              }
            : { background: '#595959' }
        "
      />

      <!-- HOME main section with clip-path and gradient background -->
      <div class="relative w-[70px] h-full overflow-hidden">
        <!-- Background with 30% opacity -->
        <div
          class="absolute inset-0 clip-path-bottom-right"
          :style="
            homeTeam && homeTeam.firstColorHEX
              ? {
                  background: `${homeTeam.firstColorHEX}`,
                  opacity: 0.3,
                }
              : { background: '#2E2E2E' }
          "
        />

        <!-- Logo at full opacity -->
        <div class="absolute inset-0 flex items-center justify-center">
          <nuxt-img
            v-if="homeTeam"
            :src="setAliasUrl(homeTeam.logo?.url) || ''"
            :alt="homeTeam.completeName || ''"
            class="w-12 h-12 object-contain"
            width="48"
            height="48"
            loading="lazy"
          />
          <span v-else class="text-xs text-gray-400 font-bold">?</span>
        </div>
      </div>
    </div>

    <!-- Center section: VS or scores (fixed minimum width) -->
    <div class="absolute flex items-center justify-center min-w-[55px]">
      <!-- Show VS only when no scores available -->
      <div
        v-if="!match || (match.scores?.homeScore === null && match.scores?.awayScore === null)"
        class="text-primary font-bold text-lg"
      >
        VS
      </div>

      <!-- Show scores when available -->
      <div v-else class="flex items-center gap-1 text-white text-lg">
        <!-- HOME penalties -->
        <span v-if="typeof match.scores?.homeScoreP === 'number'" class="text-sm font-bold text-primary">
          {{ match.scores.homeScoreP }}
        </span>

        <!-- HOME score -->
        <span class="font-bold">
          {{ match.scores?.homeScore ?? "-" }}
        </span>

        <span class="text-sm text-gray-400">-</span>

        <!-- AWAY score -->
        <span class="font-bold">
          {{ match.scores?.awayScore ?? "-" }}
        </span>

        <!-- AWAY penalties -->
        <span v-if="typeof match.scores?.awayScoreP === 'number'" class="text-sm font-bold text-primary">
          {{ match.scores.awayScoreP }}
        </span>
      </div>
    </div>

    <!-- AWAY box (right side) -->
    <div
      v-if="
        match?.metaInformation.extra_card &&
        match?.metaInformation.extra_card.split(',').some(id => id === awayTeam?.id.toString())
      "
    >
      <img
        src="/img/secret-card/extra-card-kings.png"
        alt="Secret Card"
        class="w-9 h-4 object-cover object-top absolute ltr:right-4 rtl:left-4 bottom-full"
        loading="lazy"
      />
    </div>
    <div class="absolute ltr:right-0 rtl:left-0 top-0 bottom-0 flex">
      <!-- AWAY main section with clip-path and gradient background -->
      <div class="relative w-[70px] h-full overflow-hidden">
        <!-- Background with 30% opacity -->
        <div
          class="absolute inset-0 clip-path-bottom-left"
          :style="
            awayTeam && awayTeam.firstColorHEX
              ? {
                  background: `${awayTeam.firstColorHEX}`,
                  opacity: 0.3,
                }
              : { background: '#2E2E2E' }
          "
        />

        <!-- Logo at full opacity -->
        <div class="absolute inset-0 flex items-center justify-center">
          <nuxt-img
            v-if="awayTeam"
            :src="setAliasUrl(awayTeam.logo?.url) || ''"
            :alt="awayTeam.completeName || ''"
            class="w-12 h-12 object-contain"
            width="48"
            height="48"
            loading="lazy"
          />
          <span v-else class="text-xs text-gray-400 font-bold">?</span>
        </div>
      </div>

      <!-- AWAY lateral stripe with skew -->
      <div
        class="w-[6.7px] h-full ltr:-skew-x-6 rtl:skew-x-6 -ms-1 z-[2]"
        :style="
          awayTeam && awayTeam.firstColorHEX
            ? {
                background: `${awayTeam.firstColorHEX}`,
              }
            : { background: '#595959' }
        "
      />
    </div>
  </component>
</template>

<style scoped>
.clip-path-bottom-right {
  clip-path: polygon(0 0, 90% 0, 100% 100%, 0 100%);
}

.clip-path-bottom-right:where([dir="rtl"], [dir="rtl"] *) {
  clip-path: polygon(10% 0, 100% 0, 100% 100%, 0 100%);
}

.clip-path-bottom-left {
  clip-path: polygon(10% 0, 100% 0, 100% 100%, 0 100%);
}

.clip-path-bottom-left:where([dir="rtl"], [dir="rtl"] *) {
  clip-path: polygon(0 0, 90% 0, 100% 100%, 0 100%);
}
</style>
